import { useState, useRef, useEffect } from 'react';
import UrlAnalyzeForm from './UrlAnalyzeForm';
import ErrorState from './ErrorState';
import WebsiteHealthReport from './WebsiteHealthReport';

import { analyzeUrl, pollScanStatus, extractErrorMessages } from '../../api/brandingApi';
import type { ScanType, BrandingAnalyzeResponse } from '../../api/brandingApi';
import EmailReportCapture from './EmailReportCapture';

type WidgetMode = 'idle' | 'loading' | 'slow' | 'result' | 'error';

interface Props {
  scanType: ScanType;
  formLabel?: string;
  formPlaceholder?: string;
  defaultUrl?: string;
}

export default function ToolWidget({ scanType, formLabel, formPlaceholder, defaultUrl }: Props) {
  const [mode, setMode]             = useState<WidgetMode>('idle');
  const [result, setResult]         = useState<BrandingAnalyzeResponse | null>(null);
  const [slowScan, setSlowScan]     = useState<BrandingAnalyzeResponse | null>(null);
  const [errorMsg, setErrorMsg]     = useState('');
  const [elapsedSecs, setElapsedSecs] = useState(0);
  const [scanUrl, setScanUrl]       = useState('');   // URL currently being analysed
  const abortRef                    = useRef<AbortController | null>(null);
  const pollRef                     = useRef<ReturnType<typeof setInterval> | null>(null);
  const tickRef                     = useRef<ReturnType<typeof setInterval> | null>(null);

  // Best-effort domain extraction for display labels
  const scanDomain = (() => {
    try { return new URL(scanUrl).hostname.replace(/^www\./, ''); } catch { return scanUrl; }
  })();

  // Elapsed-seconds ticker — starts from the very first loading second,
  // keeps running through slow mode, stops only when result is in hand.
  useEffect(() => {
    const active = (mode === 'loading') || (mode === 'slow' && result === null);
    if (!active) {
      if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
      return;
    }
    tickRef.current = setInterval(() => setElapsedSecs(s => s + 1), 1000);
    return () => { if (tickRef.current) clearInterval(tickRef.current); };
  }, [mode, result]);

  // Poll every 4s while in slow mode.
  // When the scan completes, store the result but STAY in 'slow' mode so the
  // email-capture form is never disrupted — results are revealed below it.
  useEffect(() => {
    if (mode !== 'slow' || !slowScan?.scanId) return;
    pollRef.current = setInterval(async () => {
      try {
        const data = await pollScanStatus(slowScan.scanId);
        if (data.status !== 'RUNNING') {
          clearInterval(pollRef.current!);
          setResult(data);
          // intentionally NOT calling setMode('result') here —
          // the slow block detects result !== null and appends the report below
          // the email form without any jarring replacement.
        }
      } catch {
        // network blip — silently retry next interval
      }
    }, 4000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [mode, slowScan?.scanId]);

  const handleAnalyze = async ({
    name,
    email,
    businessName,
    url,
  }: { name: string; email: string; businessName: string; url: string }) => {
    abortRef.current?.abort();
    abortRef.current = new AbortController();
    if (pollRef.current) clearInterval(pollRef.current);

    setScanUrl(url);
    setMode('loading');
    setResult(null);
    setSlowScan(null);
    setElapsedSecs(0);
    setErrorMsg('');

    try {
      const data = await analyzeUrl({ name, email, businessName, url, scanType }, abortRef.current.signal);
      if (data.status === 'RUNNING') {
        setSlowScan(data);
        setMode('slow');
      } else {
        setResult(data);
        setMode('result');
      }
    } catch (err) {
      const msg = extractErrorMessages(err);
      if (msg === '') return;
      setErrorMsg(msg);
      setMode('error');
    }
  };

  const handleScanAgain = () => {
    abortRef.current?.abort();
    if (pollRef.current) clearInterval(pollRef.current);
    setMode('idle');
    setResult(null);
    setSlowScan(null);
    setErrorMsg('');
  };

  // Standard report shell used by the fast and slow completion paths.
  const ResultsPanel = ({
    res,
    showEmail = false,
  }: {
    res: BrandingAnalyzeResponse;
    showEmail?: boolean;
  }) => (
    <WebsiteHealthReport
      result={res}
      scanType={scanType}
      showEmail={showEmail}
      onScanAgain={handleScanAgain}
    />
  );

  return (
    <div>
      {/* URL form — visible for idle and fast-path result */}
      {(mode === 'idle' || mode === 'result') && (
        <div style={{ marginBottom: '24px' }}>
          <UrlAnalyzeForm
            key={result?.scanId ?? 'new'}
            label={formLabel}
            placeholder={formPlaceholder}
            loading={false}
            defaultUrl={defaultUrl}
            initialName={result?.customerName}
            initialEmail={result?.customerEmail}
            initialBusinessName={result?.businessName}
            onSubmit={handleAnalyze}
          />
        </div>
      )}

      {/* ── LOADING (0s → ~8s) ──────────────────────────────────────────────
          Spinner + elapsed counter visible from the very first second. */}
      {mode === 'loading' && (
        <div style={{ marginTop: '8px' }}>
          <div style={{ display: 'flex', gap: '24px', alignItems: 'flex-start', flexWrap: 'wrap', marginBottom: '16px' }}>
            {/* Large spinner with elapsed seconds in center */}
            <div className="asneris-spinner-wrap" style={{ width: 110, height: 110, flexShrink: 0 }}>
              <span className="asneris-spinner asneris-spinner--lg" />
              <div className="asneris-spinner-label">
                {elapsedSecs}
                <span>sec</span>
              </div>
            </div>
            {/* Skeleton summary area */}
            <div style={{ flex: 1, minWidth: 180, paddingTop: '4px' }}>
              <span className="asneris-skeleton" style={{ width: 160, height: 24, marginBottom: '8px' }} />
              <span className="asneris-skeleton" style={{ width: '90%', height: 16, marginBottom: '6px' }} />
              <span className="asneris-skeleton" style={{ width: '75%', height: 16, marginBottom: '8px' }} />
              <p style={{ margin: 0, fontSize: '12px', color: 'var(--asneris-muted)' }}>
                Analysing <strong>{scanDomain}</strong>…
              </p>
            </div>
          </div>
          {[...Array(4)].map((_, i) => (
            <span key={i} className="asneris-skeleton" style={{ height: '48px', display: 'block', marginBottom: '8px' }} />
          ))}
        </div>
      )}

      {mode === 'error' && (
        <ErrorState message={errorMsg} onRetry={handleScanAgain} />
      )}

      {/* ── SLOW PATH (202 RUNNING) ──────────────────────────────────────────
          Email form first and most prominent — primary lead-capture moment.
          When result arrives: email form stays, report slides in below it. */}
      {mode === 'slow' && slowScan && (
        <div>
          {/* ── EMAIL CAPTURE — primary action, always at the top ── */}
          {!result && (
            <EmailReportCapture
              scanId={slowScan.scanId}
              domain={slowScan.domain ?? ''}
              variant="slow-path"
              defaultEmail={slowScan?.customerEmail ?? ''}
            />
          )}

          {/* ── Completion banner (replaces email prompt header once done) ── */}
          {result && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <circle cx="12" cy="12" r="10" stroke="var(--asneris-green)" strokeWidth="2" />
                  <polyline points="7,13 10,16 17,9" stroke="var(--asneris-green)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <p style={{ margin: 0, fontSize: '14px', color: 'var(--asneris-green)', fontWeight: 500 }}>
                  Report ready for <strong>{slowScan.domain}</strong> — view below or send to your inbox.
                </p>
              </div>
              <EmailReportCapture
                scanId={slowScan.scanId}
                domain={slowScan.domain ?? ''}
                variant="slow-path"
                defaultEmail={slowScan?.customerEmail ?? ''}
              />
            </>
          )}

          {/* ── Spinner + skeleton while still waiting ── */}
          {!result && (
            <div style={{ marginTop: '16px' }}>
              <div style={{ display: 'flex', gap: '24px', alignItems: 'flex-start', flexWrap: 'wrap', marginBottom: '16px' }}>
                <div className="asneris-spinner-wrap" style={{ width: 110, height: 110, flexShrink: 0 }}>
                  <span className="asneris-spinner asneris-spinner--lg asneris-spinner--warning" />
                  <div className="asneris-spinner-label">
                    {elapsedSecs}
                    <span>sec</span>
                  </div>
                </div>
                <div style={{ flex: 1, minWidth: 180, paddingTop: '4px' }}>
                  <span className="asneris-skeleton" style={{ width: 160, height: 24, marginBottom: '8px' }} />
                  <span className="asneris-skeleton" style={{ width: '90%', height: 16, marginBottom: '6px' }} />
                  <span className="asneris-skeleton" style={{ width: '75%', height: 16, marginBottom: '8px' }} />
                  <p style={{ margin: 0, fontSize: '12px', color: 'var(--asneris-muted)' }}>
                    Still analysing <strong>{slowScan.domain}</strong>…
                  </p>
                </div>
              </div>
              {[...Array(4)].map((_, i) => (
                <span key={i} className="asneris-skeleton" style={{ height: '48px', display: 'block', marginBottom: '8px' }} />
              ))}
              <button className="asneris-btn asneris-btn-secondary asneris-btn-sm" type="button" style={{ marginTop: '4px' }} onClick={handleScanAgain}>
                Cancel
              </button>
            </div>
          )}

          {/* ── Results slide in below the email form — no replacement, no jump ── */}
          {result && (
            <div style={{ marginTop: '24px' }}>
              <hr className="asneris-divider" />
              <ResultsPanel res={result} showEmail={false} />
            </div>
          )}
        </div>
      )}

      {/* ── FAST PATH (200 immediately, < 8s) ───────────────────────────────
          showEmail=true inserts the capture form between score and check details. */}
      {mode === 'result' && result && (
        <div>
          <ResultsPanel res={result} showEmail={true} />
        </div>
      )}
    </div>
  );
}

